import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import CustomerService, { Customer } from '../../services/customer.service';
import { NumberTextField } from '../../components/common';
import { servicePackageService } from '../../services/servicePackageService';

const CustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [servicePackages, setServicePackages] = useState<any[]>([]);
  const [formData, setFormData] = useState<Customer>({
    customerNumber: '',
    contactPerson: '',
    email: '',
    customerType: 'business',
    isActive: true,
  });

  useEffect(() => {
    fetchServicePackages();
    if (isEdit && id) {
      fetchCustomer(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchServicePackages = async () => {
    try {
      const data = await servicePackageService.getAll();
      setServicePackages(data);
    } catch (error) {
      console.error('Error fetching service packages:', error);
    }
  };

  const fetchCustomer = async (customerId: number) => {
    try {
      const response = await CustomerService.getOne(customerId);
      const customer = response.data?.data?.attributes || response.data?.data || response.data;
      if (customer) {
        // Handle servicePackage relation
        const servicePackageId = customer.servicePackage?.data?.id || customer.servicePackage?.id || customer.servicePackage;
        
        // Format dates for date inputs (YYYY-MM-DD format)
        const formatDateForInput = (dateString: string | null | undefined) => {
          if (!dateString) return '';
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
          } catch {
            return '';
          }
        };
        
        setFormData({
          ...customer,
          servicePackage: servicePackageId,
          vinNumber: customer.vinNumber || '',
          plateNumber: customer.plateNumber || '',
          carMake: customer.carMake || '',
          carModel: customer.carModel || '',
          carYear: customer.carYear || '',
          policyNumber: customer.policyNumber || '',
          policyStartDate: formatDateForInput(customer.policyStartDate),
          policyEndDate: formatDateForInput(customer.policyEndDate),
        });
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value ? parseInt(value) : null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Clean form data - convert empty strings to null for optional fields
      const cleanedData: any = { ...formData };
      
      // Convert empty strings to null for optional fields
      const optionalFields = ['phone', 'mobile', 'address', 'city', 'country', 'taxNumber', 'paymentTerms', 'notes', 'website', 'servicePackage', 'vinNumber', 'plateNumber', 'carMake', 'carModel', 'carYear', 'policyNumber', 'policyStartDate', 'policyEndDate'];
      optionalFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === undefined) {
          cleanedData[field] = null;
        }
      });

      // Ensure servicePackage and carYear are integers or null
      if (cleanedData.servicePackage) {
        cleanedData.servicePackage = parseInt(String(cleanedData.servicePackage));
      } else {
        cleanedData.servicePackage = null;
      }

      if (cleanedData.carYear) {
        cleanedData.carYear = parseInt(String(cleanedData.carYear));
      } else {
        cleanedData.carYear = null;
      }
      
      // Ensure date fields are properly formatted or null
      if (cleanedData.policyStartDate && cleanedData.policyStartDate.trim() !== '') {
        // Date is already in YYYY-MM-DD format from input
        cleanedData.policyStartDate = cleanedData.policyStartDate;
      } else {
        cleanedData.policyStartDate = null;
      }
      
      if (cleanedData.policyEndDate && cleanedData.policyEndDate.trim() !== '') {
        // Date is already in YYYY-MM-DD format from input
        cleanedData.policyEndDate = cleanedData.policyEndDate;
      } else {
        cleanedData.policyEndDate = null;
      }

      if (isEdit && id) {
        await CustomerService.update(parseInt(id), cleanedData);
      } else {
        await CustomerService.create(cleanedData);
      }
      navigate('/customers');
    } catch (err: any) {
      console.error('Error saving customer:', err);
      setError(err.response?.data?.error?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Customer' : 'Create Customer'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Customer Number"
                name="customerNumber"
                value={formData.customerNumber}
                onChange={handleChange}
                disabled={isEdit}
              />
              <TextField
                fullWidth
                required
                label="Contact Person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={formData.mobile || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                select
                label="Customer Type"
                name="customerType"
                value={formData.customerType || 'business'}
                onChange={handleChange}
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="business">Business</MenuItem>
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formData.companyName || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Tax Number"
                name="taxNumber"
                value={formData.taxNumber || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                label="Credit Limit"
                name="creditLimit"
                value={formData.creditLimit || 0}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formData.website || ''}
                onChange={handleChange}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Service Package</InputLabel>
              <Select
                name="servicePackage"
                value={formData.servicePackage || ''}
                onChange={handleSelectChange}
                label="Service Package"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {servicePackages.map((pkg) => (
                  <MenuItem key={pkg.id} value={pkg.id}>
                    {pkg.attributes?.name || pkg.name} ({pkg.attributes?.code || pkg.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Vehicle Information</Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="VIN Number"
                name="vinNumber"
                value={formData.vinNumber || ''}
                onChange={handleChange}
                placeholder="Enter VIN number..."
              />
              <TextField
                fullWidth
                label="Plate Number"
                name="plateNumber"
                value={formData.plateNumber || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Car Make"
                name="carMake"
                value={formData.carMake || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Car Model"
                name="carModel"
                value={formData.carModel || ''}
                onChange={handleChange}
              />
              <NumberTextField
                fullWidth
                label="Car Year"
                name="carYear"
                value={formData.carYear || ''}
                onChange={handleChange}
                inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                allowDecimal={false}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Policy Information (Optional)</Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Policy Number"
                name="policyNumber"
                value={formData.policyNumber || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Policy Start Date"
                name="policyStartDate"
                type="date"
                value={formData.policyStartDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Policy End Date"
                name="policyEndDate"
                type="date"
                value={formData.policyEndDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Address"
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={formData.isActive !== false}
                  onChange={handleChange}
                />
              }
              label="Active"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/customers')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default CustomerForm;
